// Auth wrapper — mirrors the pattern used on playaware.gg / the mobile
// app: Google OAuth (one click) + email+password (with reset). No more
// email-link mechanism.
//
// Same Firebase project as PlayAware mobile + playaware-content, so a
// user signing in here reuses their existing app_users/{uid} record —
// no data forks between web surfaces.

import {
  onAuthStateChanged,
  signOut as fbSignOut,
  signInAnonymously,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile,
} from "firebase/auth";
import {
  doc, getDoc, getDocFromServer, setDoc, serverTimestamp,
} from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";
import { assignUniqueUsername } from "../utils/username";

// Re-exported for pages that need them directly.
export {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
};

export function subscribeAuth(cb) {
  return onAuthStateChanged(auth, cb);
}

export function currentUser() {
  return auth.currentUser;
}

export async function signOut() {
  await fbSignOut(auth);
}

export async function anonSignIn() {
  return signInAnonymously(auth);
}

// ── Provider methods ────────────────────────────────────────────────────────

export async function signInWithGoogle() {
  const cred = await signInWithPopup(auth, googleProvider);
  await ensureUserDoc(cred.user);
  return cred;
}

export async function signInWithPassword(email, password) {
  const cred = await signInWithEmailAndPassword(
      auth, email.trim().toLowerCase(), password);
  await ensureUserDoc(cred.user);
  return cred;
}

export async function createAccountWithPassword(email, password, displayName) {
  const cred = await createUserWithEmailAndPassword(
      auth, email.trim().toLowerCase(), password);
  if (displayName && displayName.trim()) {
    await updateProfile(cred.user, { displayName: displayName.trim() });
  }
  await ensureUserDoc(cred.user);
  return cred;
}

export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email.trim().toLowerCase());
}

// ── Helpers ─────────────────────────────────────────────────────────────────

// Wait until Firebase has restored auth state from IndexedDB before
// making routing decisions. Fixes the redirect-to-signin bounce that
// otherwise happens on cold page loads.
export async function waitForAuth() {
  if (typeof auth.authStateReady === "function") {
    await auth.authStateReady();
  } else {
    await new Promise((resolve) => {
      const unsub = onAuthStateChanged(auth, () => { unsub(); resolve(); });
    });
  }
  return auth.currentUser;
}

// Create a stub app_users/{uid} doc on first sign-in. Mirrors the
// same helper on playaware-content so a Pop Market user has a valid
// record when they later open the mobile app or content site with
// the same Google account.
//
// Also assigns a globally-unique `username` (reserved in `Usernames/`)
// so the same account is reachable at /u/{username} across surfaces.
// popMarketRole is left null until the role picker sets it.
export async function ensureUserDoc(user) {
  if (!user) return;
  const ref = doc(db, "app_users", user.uid);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    // Backfill username on legacy docs created before this shipped.
    if (!snap.data()?.username) {
      const username = await assignUniqueUsername(user);
      await setDoc(ref, { username, updatedAt: serverTimestamp() }, { merge: true });
    }
    return;
  }

  const username = await assignUniqueUsername(user);
  await setDoc(ref, {
    uid: user.uid,
    email: user.email || "",
    displayName: user.displayName || (user.email || "").split("@")[0],
    photoURL: user.photoURL || "",
    username,
    popMarketRole: null,
    popMarketContact: null,
    createdAt: serverTimestamp(),
    createdVia: "pop-market",
  });
}

// Read the current user's app_users doc — used by auth flows to decide
// whether the role picker is still needed after sign-in.
//
// Uses getDocFromServer to bypass the persistent local cache. A
// hanging read was traced to stale IndexedDB state from
// persistentLocalCache (see firebase.js) — the server round-trip
// short-circuits that.
export async function readAppUser(uid) {
  if (!uid) return null;
  try {
    const snap = await getDocFromServer(doc(db, "app_users", uid));
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    // If the server is unreachable (rare — only offline), fall back
    // to the cached read so we at least return something.
    const snap = await getDoc(doc(db, "app_users", uid));
    return snap.exists() ? snap.data() : null;
  }
}

// Friendly Firebase error → user-readable string. Same shape as the
// content-site helper.
export function authErrMsg(err) {
  const code = err?.code || "";
  if (code.includes("user-not-found")) return "No account with this email.";
  if (code.includes("wrong-password")) return "Wrong password.";
  if (code.includes("invalid-credential")) return "Wrong email or password.";
  if (code.includes("email-already-in-use")) return "An account already exists with this email — sign in instead.";
  if (code.includes("weak-password")) return "Password must be at least 6 characters.";
  if (code.includes("invalid-email")) return "That email doesn't look right.";
  if (code.includes("popup-closed")) return "Sign-in cancelled.";
  if (code.includes("popup-blocked")) return "Your browser blocked the sign-in popup. Allow popups and try again.";
  if (code.includes("network")) return "Network error — check your connection.";
  if (code.includes("too-many-requests")) return "Too many attempts — try again in a few minutes.";
  return err?.message || "Something went wrong. Try again.";
}
